import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Target, TrendingUp, Users, Zap, ArrowRight, CheckCircle } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import CaseStudies, { demandGenReviews } from "@/components/CaseStudies";

const DemandGeneration = () => {
  const benefits = [
    "Predictable pipeline growth month over month",
    "Multi-channel campaign orchestration",
    "Intent data-driven targeting",
    "Full-funnel attribution tracking",
    "Automated lead nurturing sequences",
    "Sales and marketing alignment",
  ];

  const process = [
    {
      icon: Target,
      title: "ICP Definition",
      description: "We identify your ideal customer profile using firmographic, technographic, and intent data.",
    },
    {
      icon: Users,
      title: "Audience Building",
      description: "Build high-intent audiences across LinkedIn, programmatic, and content syndication channels.",
    },
    {
      icon: Zap,
      title: "Campaign Activation",
      description: "Launch coordinated campaigns that reach buyers at every stage of their journey.",
    },
    {
      icon: TrendingUp,
      title: "Optimize & Scale",
      description: "Continuously optimize based on pipeline contribution, not vanity metrics.",
    },
  ];

  return (
    <>
      <EnhancedSEO
        type="service"
        title="B2B Demand Generation | VGP - Pipeline-Focused Marketing"
        description="Drive predictable pipeline with multi-channel demand generation. Intent data targeting, content syndication, and full-funnel campaigns that convert."
        keywords="B2B demand generation, pipeline marketing, intent data, content syndication, lead generation"
        url="/demand-generation"
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-6">
                Demand Generation
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Build Pipeline That <span className="text-primary">Actually Closes</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Stop chasing MQLs. Start building predictable, qualified pipeline with demand generation that's measured by revenue, not leads.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="premium" asChild>
                  <Link to="/do-you-qualify">
                    Get Pipeline Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">See Investment</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                What You Get
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">
                Our Demand Gen Process
              </h2>
              <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                A systematic approach to building pipeline that compounds over time.
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 text-center hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Section */}
        <CaseStudies 
          title="Real Demand Generation Results"
          subtitle="See how we've helped B2B companies build predictable pipeline."
          reviews={demandGenReviews}
        />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Build Predictable Pipeline?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Get a strategic assessment and see exactly where your demand generation can improve.
              </p>
              <Button size="lg" variant="premium" asChild>
                <Link to="/do-you-qualify">
                  Schedule Strategic Review
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DemandGeneration;